import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Upload, FileText, Loader2, CheckCircle2, RefreshCw, Sparkles, Send } from "lucide-react";

/**
 * PriceLedgerAutoUpdate — Admin drops in a monthly price PDF or DOCX and
 * the pipeline extracts rates via Gemini, stages a preview, then lets the
 * admin publish it. The Home page's Monthly Price Pulse re-fetches
 * `/api/price-ledger/latest` and switches to the new month automatically.
 */
export function PriceLedgerAutoUpdate({ api }) {
  const fileRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const [jobId, setJobId] = useState(null);
  const [state, setState] = useState(null); // queued | extracting | ready | failed
  const [ledger, setLedger] = useState(null);
  const [history, setHistory] = useState([]);
  const [autoBroadcast, setAutoBroadcast] = useState(true);
  const [broadcasting, setBroadcasting] = useState(null); // month_key

  const loadHistory = async () => {
    try {
      const r = await api.get("/admin/price-ledger/all");
      setHistory(r.data?.ledgers || []);
    } catch { setHistory([]); }
  };

  useEffect(() => { loadHistory(); }, []);   // eslint-disable-line

  // Poll job every 4 s until ready/failed
  useEffect(() => {
    if (!jobId || state === "ready" || state === "failed") return;
    const t = setInterval(async () => {
      try {
        const r = await api.get(`/admin/price-ledger/job/${jobId}`);
        setState(r.data?.state);
        if (r.data?.state === "ready") {
          setLedger(r.data?.ledger || null);
          loadHistory();
          clearInterval(t);
        } else if (r.data?.state === "failed") {
          toast.error(r.data?.error || "Extraction failed. Try a cleaner PDF.");
          clearInterval(t);
        }
      } catch { /* ignore transient */ }
    }, 4000);
    return () => clearInterval(t);
  }, [jobId, state]);   // eslint-disable-line

  const onFile = async (file) => {
    if (!file) return;
    if (file.size > 15 * 1024 * 1024) { toast.error("Max 15 MB"); return; }
    setBusy(true); setLedger(null); setJobId(null); setState(null);
    const form = new FormData();
    form.append("file", file);
    try {
      const r = await api.post("/admin/price-ledger/upload", form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setJobId(r.data?.job_id);
      setState(r.data?.queued ? "queued" : "extracting");
      toast.success(`Queued for ${r.data?.hint_label || "extraction"}. This takes ~30-60 seconds.`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Upload failed");
    } finally {
      setBusy(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const publish = async () => {
    if (!ledger?.month_key) return;
    try {
      const r = await api.post("/admin/price-ledger/publish", {
        month_key: ledger.month_key,
        auto_broadcast: autoBroadcast,
      });
      toast.success(
        autoBroadcast
          ? `${ledger.month_label} published — network broadcast queued.`
          : `${ledger.month_label} published — Home card updated.`
      );
      setLedger((L) => (L ? { ...L, published: true } : L));
      loadHistory();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Publish failed");
    }
  };

  const broadcastNow = async (mk, label) => {
    setBroadcasting(mk);
    try {
      const r = await api.post(`/admin/price-ledger/broadcast/${mk}`);
      if (r.data?.queued) {
        toast.success(`${label || "Ledger"} · broadcast queued — this can take a couple of minutes.`);
      } else if (r.data?.emailed !== undefined) {
        toast.success(`${label || "Ledger"} · Emailed ${r.data.emailed} · Failed ${r.data.failed}`);
      }
      // Poll history a couple of times so the "sent N" pill appears.
      setTimeout(loadHistory, 4000);
      setTimeout(loadHistory, 30000);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Broadcast failed");
    } finally { setBroadcasting(null); }
  };

  const republish = async (mk) => {
    try {
      await api.post("/admin/price-ledger/publish", { month_key: mk });
      toast.success("Ledger re-published.");
      loadHistory();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const s = ledger?.summary || {};
  const stateLabel = {
    queued: "Queued",
    extracting: "Reading document · Gemini is parsing rows",
    ready: "Ready to review",
    failed: "Failed",
  };

  return (
    <div
      data-testid="price-ledger-auto-update"
      className="mb-8 rounded-sm border border-[#2FB999]/40 bg-gradient-to-r from-[#2FB999]/[0.06] to-transparent px-5 py-5"
    >
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="min-w-0">
          <div className="font-grotesk text-[10px] uppercase tracking-[0.24em] text-[#2FB999]">
            Monthly Ledger · Auto-Update
          </div>
          <div className="font-serif text-xl md:text-2xl text-[#F5F5F0] mt-1">
            Scan a price PDF or Word — publish in one tap
          </div>
          <div className="font-body text-xs text-[#8A8A82] mt-1 max-w-xl">
            Drop in your monthly price list. Gemini extracts every steel &amp; cement row and stages a preview.
            Approve to update the Home Monthly Price Pulse instantly.
          </div>
        </div>
        <div className="flex flex-wrap gap-2">
          <input
            ref={fileRef}
            type="file"
            accept=".pdf,.docx,application/pdf,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
            className="hidden"
            data-testid="ledger-upload-file"
            onChange={(e) => onFile(e.target.files?.[0])}
          />
          <button
            data-testid="ledger-upload-btn"
            disabled={busy || state === "queued" || state === "extracting"}
            onClick={() => fileRef.current?.click()}
            className="rounded-full bg-[#2FB999] text-black px-5 py-2.5 font-grotesk text-[10px] uppercase tracking-[0.22em] hover:brightness-110 disabled:opacity-50 flex items-center gap-2"
          >
            {busy ? <Loader2 size={12} className="animate-spin" /> : <Upload size={12} />}
            Auto-Update from PDF / Word
          </button>
          <button
            data-testid="ledger-refresh-history"
            onClick={loadHistory}
            className="rounded-full border border-white/20 hover:border-[#2FB999] text-[#C9C9C2] hover:text-[#2FB999] px-4 py-2.5 font-grotesk text-[10px] uppercase tracking-[0.22em] flex items-center gap-2"
          >
            <RefreshCw size={12} /> Refresh
          </button>
        </div>
      </div>

      {/* Progress / preview */}
      {(state === "queued" || state === "extracting") && (
        <div data-testid="ledger-progress" className="mt-5 flex items-center gap-3 rounded-sm border border-white/10 bg-[#0a0a0a]/70 px-4 py-3">
          <Loader2 size={14} className="animate-spin text-[#D8B23E]" />
          <span className="font-body text-sm text-[#C9C9C2]">{stateLabel[state]}…</span>
          <span className="ml-auto font-grotesk text-[10px] uppercase tracking-[0.22em] text-[#8A8A82]">
            Cloudflare-safe · takes 30-60 s
          </span>
        </div>
      )}

      {ledger && state === "ready" && (
        <div data-testid="ledger-preview" className="mt-5 rounded-sm border border-[#D8B23E]/40 bg-[#0a0a0a]/70 p-5">
          <div className="flex flex-wrap items-center justify-between gap-4 border-b border-white/10 pb-4">
            <div>
              <div className="font-grotesk text-[10px] uppercase tracking-[0.24em] text-[#D8B23E]">Extraction complete</div>
              <div className="font-serif text-2xl text-[#F5F5F0] mt-1">{ledger.month_label}</div>
              <div className="font-body text-xs text-[#8A8A82] mt-1">
                {s.total || 0} rows · {(s.brands || []).length} brands · steel {s.steel?.count || 0} · cement {s.cement?.count || 0}
              </div>
            </div>
            <div className="flex flex-wrap items-center gap-3">
              <label className="inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.2em] font-grotesk text-[#8A8A82] cursor-pointer">
                <input
                  type="checkbox"
                  data-testid="ledger-auto-broadcast"
                  checked={autoBroadcast}
                  onChange={(e) => setAutoBroadcast(e.target.checked)}
                  className="accent-[#D8B23E] w-4 h-4"
                />
                Auto-email partners on publish
              </label>
              {ledger.published ? (
                <span className="inline-flex items-center gap-2 rounded-full border border-[#2FB999]/40 bg-[#2FB999]/[0.08] px-4 py-2 font-grotesk text-[10px] uppercase tracking-[0.22em] text-[#2FB999]">
                  <CheckCircle2 size={12} /> Live on Home
                </span>
              ) : (
                <button
                  data-testid="ledger-publish-btn"
                  onClick={publish}
                  className="rounded-full bg-[#D8B23E] text-black px-5 py-2.5 font-grotesk text-[10px] uppercase tracking-[0.22em] hover:brightness-110 flex items-center gap-2"
                >
                  <Send size={12} /> Publish to Home
                </button>
              )}
            </div>
          </div>
          {/* First 8 entries preview */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-1 mt-4">
            {(ledger.entries || []).slice(0, 12).map((e, i) => (
              <div
                key={i}
                data-testid={`ledger-entry-preview-${i}`}
                className="flex items-baseline justify-between py-2 border-b border-white/5"
              >
                <div className="min-w-0">
                  <div className="font-serif text-[15px] text-[#F5F5F0] truncate">{e.brand}</div>
                  {(e.product || e.region) && (
                    <div className="font-grotesk text-[9px] uppercase tracking-[0.2em] text-[#8A8A82] mt-0.5 truncate">
                      {[e.product, e.region].filter(Boolean).join("  ·  ")}
                    </div>
                  )}
                </div>
                <div className="text-right shrink-0">
                  <div className="font-mono text-sm text-[#F5F5F0]">
                    ₹{e.price_min.toLocaleString("en-IN")}
                    {e.price_max > e.price_min && <span className="text-[#8A8A82]"> – ₹{e.price_max.toLocaleString("en-IN")}</span>}
                  </div>
                  <div className="text-[9px] uppercase tracking-[0.18em] text-[#8A8A82] font-grotesk mt-0.5">{e.unit}</div>
                </div>
              </div>
            ))}
          </div>
          {ledger.entries?.length > 12 && (
            <div className="mt-3 text-[10px] text-[#8A8A82] font-grotesk uppercase tracking-[0.22em]">
              + {ledger.entries.length - 12} more rows staged
            </div>
          )}
        </div>
      )}

      {/* Prior ledger history — enables monthly continuity */}
      {history.length > 0 && (
        <div className="mt-6 pt-5 border-t border-white/10">
          <div className="font-grotesk text-[10px] uppercase tracking-[0.24em] text-[#8A8A82] mb-3">
            Ledger history — every month you've uploaded
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="ledger-history">
            {history.map((h) => (
              <div
                key={h.month_key}
                data-testid={`ledger-history-${h.month_key}`}
                className={`rounded-sm border p-3 ${
                  h.published ? "border-[#2FB999]/40 bg-[#2FB999]/[0.04]" : "border-white/15 bg-white/[0.02]"
                }`}
              >
                <div className="flex items-center justify-between">
                  <div className="font-serif text-base text-[#F5F5F0]">{h.month_label}</div>
                  {h.published ? (
                    <CheckCircle2 size={13} className="text-[#2FB999]" />
                  ) : (
                    <Sparkles size={13} className="text-[#D8B23E]" />
                  )}
                </div>
                <div className="font-grotesk text-[9px] uppercase tracking-[0.22em] text-[#8A8A82] mt-1">
                  {h.entries?.length || 0} rows · {h.published ? "Live" : "Draft"}
                </div>
                <div className="flex gap-2 mt-2">
                  {!h.published && (
                    <button
                      data-testid={`ledger-history-publish-${h.month_key}`}
                      onClick={() => republish(h.month_key)}
                      className="text-[10px] uppercase tracking-[0.18em] font-grotesk text-[#D8B23E] hover:brightness-110"
                    >
                      Publish
                    </button>
                  )}
                  {h.published && (
                    <button
                      data-testid={`ledger-history-broadcast-${h.month_key}`}
                      disabled={broadcasting === h.month_key}
                      onClick={() => broadcastNow(h.month_key, h.month_label)}
                      className="text-[10px] uppercase tracking-[0.18em] font-grotesk text-[#2FB999] hover:brightness-110 disabled:opacity-50"
                    >
                      {broadcasting === h.month_key ? "Broadcasting…" : "📧 Broadcast to network"}
                    </button>
                  )}
                  {h.last_broadcast_stats && (
                    <span className="text-[9px] uppercase tracking-[0.16em] font-grotesk text-[#8A8A82]">
                      · sent {h.last_broadcast_stats.emailed}
                    </span>
                  )}
                  <FileText size={11} className="text-[#8A8A82] ml-auto self-center" />
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
